import { useEffect, useMemo, useRef, useState, type ReactElement } from 'react'
import {
  initializeDiscordSdk,
  type DiscordActivityContext
} from './discordSdk'
import {
  fallbackActivityConfig,
  loadActivityConfig,
  type ActivityRuntimeConfig
} from './activityConfig'
import { loadActivityLaunchContext } from './activityLaunchContext'
import {
  activitySurfaces,
  defaultSurfaceKey,
  groupedSurfaces,
  surfaceByKey,
  surfaceByRoute,
  type ActivityAction,
  type ActivitySurface
} from './activityCatalog'
import {
  surfaceDataFor,
  type SurfaceDataPayload,
  type SurfaceDataView
} from './activitySurfaceData'
import { loadActivitySurfaceData } from './activitySurfaceDataClient'
import {
  createPreviewSnapshot,
  loadMatchroomSnapshot,
  mutateMatchroom,
  type ActivityPlayer,
  type MapState,
  type MapPoolItem,
  type MatchroomSnapshot,
  type ReadyState
} from './matchroomState'

const previewSnapshot = createPreviewSnapshot()

export default function App(): ReactElement {
  const [activityConfig, setActivityConfig] =
    useState<ActivityRuntimeConfig>(fallbackActivityConfig)
  const [context, setContext] = useState<DiscordActivityContext>({
    status: 'local',
    label: 'Inicializando'
  })
  const [activeSurface, setActiveSurface] = useState<ActivitySurface>(() =>
    surfaceByRoute(window.location.pathname)
  )
  const [actionFeedback, setActionFeedback] = useState(
    'Selecione uma superficie para operar a Activity.'
  )
  const initialPathRef = useRef(window.location.pathname)
  const userNavigatedRef = useRef(false)

  useEffect(() => {
    let mounted = true
    loadActivityConfig().then(async (loadedConfig) => {
      if (!mounted) return
      setActivityConfig(loadedConfig)
      const next = await initializeDiscordSdk(loadedConfig.discord.clientId)
      if (!mounted) return
      setContext(next)
    })
    return () => {
      mounted = false
    }
  }, [])

  useEffect(() => {
    const listener = (): void => {
      userNavigatedRef.current = true
      setActiveSurface(surfaceByRoute(window.location.pathname))
    }
    window.addEventListener('popstate', listener)
    return () => window.removeEventListener('popstate', listener)
  }, [])

  useEffect(() => {
    let mounted = true
    if (context.status !== 'ready') return () => {
      mounted = false
    }
    if (!canApplyLaunchContext(initialPathRef.current) || userNavigatedRef.current) {
      return () => {
        mounted = false
      }
    }

    loadActivityLaunchContext(context).then((loaded) => {
      if (!mounted || !loaded || userNavigatedRef.current) return
      selectSurface(loaded.surface, 'system')
      if (loaded.fallback) {
        setActionFeedback(`${loaded.surface.title}: entrada padrao da Activity.`)
        return
      }
      const source = [loaded.sourceCommand, loaded.sourceSubcommand]
        .filter(Boolean)
        .join(' ')
      setActionFeedback(
        `${loaded.surface.title}: aberto pelo contexto ${source || 'do Discord'}.`
      )
    })
    return () => {
      mounted = false
    }
  }, [context])

  function selectSurface(
    surface: ActivitySurface,
    source: 'user' | 'system' = 'user'
  ): void {
    if (source === 'user') userNavigatedRef.current = true
    setActiveSurface(surface)
    window.history.pushState({}, '', surface.route)
    setActionFeedback(`${surface.title}: superficie selecionada.`)
  }

  const manifestSurfaceKeys = new Set(activityConfig.surfaces.map((item) => item.key))
  const runtimeConnected = context.status === 'ready' && activityConfig.activity.runtimeConfigured
  const appName = activityConfig.application.name
  const connectionLabel =
    context.status === 'ready'
      ? 'Discord autenticado'
      : context.status === 'error'
        ? context.label
        : 'Preview local'

  return (
    <main className="activity-console" data-surface={activeSurface.key}>
      <header className="console-topbar">
        <div className="brand-lockup">
          <div className="brand-mark" aria-hidden="true">
            <img src="/striker-arena-icon-512.png" alt="" />
          </div>
          <div>
            <h1>{appName} Activity</h1>
            <span>
              Arena Pro shell · {activitySurfaces.length} superficies ·{' '}
              {manifestSurfaceKeys.size} no manifesto
            </span>
          </div>
        </div>
        <div className="topbar-status">
          <span className={`pulse ${context.status}`} />
          <span>{connectionLabel}</span>
        </div>
        <button
          className="primary"
          data-activity-button="topbar-home"
          onClick={() => selectSurface(surfaceByKey(defaultSurfaceKey))}
        >
          Voltar para inicio
        </button>
      </header>

      <aside className="surface-nav" aria-label="Superficies da Activity">
        {groupedSurfaces().map((group) => (
          <section key={group.group}>
            <h2>{group.group}</h2>
            {group.surfaces.map((surface) => (
              <button
                key={surface.key}
                className={surface.key === activeSurface.key ? 'selected' : ''}
                data-surface-nav={surface.key}
                onClick={() => selectSurface(surface)}
              >
                <SurfaceIcon surface={surface} />
                <span className="surface-nav-label">{surface.shortTitle}</span>
                <strong>{maturityLabel(surface.maturity)}</strong>
              </button>
            ))}
          </section>
        ))}
      </aside>

      <section className="surface-stage">
        <SurfaceHeader
          surface={activeSurface}
          manifestEnabled={manifestSurfaceKeys.has(activeSurface.key)}
          runtimeConnected={runtimeConnected}
        />
        {activeSurface.key === 'matchroom' || activeSurface.key === 'map-veto' ? (
          <MatchroomSurface
            context={context}
            activityConfig={activityConfig}
            mapOnly={activeSurface.key === 'map-veto'}
            onActionFeedback={setActionFeedback}
          />
        ) : (
          <SurfaceWorkbench
            surface={activeSurface}
            context={context}
            onSelectSurface={selectSurface}
            onActionFeedback={setActionFeedback}
          />
        )}
      </section>

      <aside className="coverage-panel">
        <h2>Plano de cobertura</h2>
        <CoverageSummary />
        <div className="coverage-box">
          <span>Foco atual</span>
          <strong>{activeSurface.testingFocus.join(' · ')}</strong>
        </div>
        <div className="coverage-box">
          <span>Comandos cobertos</span>
          <strong>{activeSurface.commands.join(', ')}</strong>
        </div>
        <div className="coverage-box">
          <span>Status</span>
          <strong>{maturityDescription(activeSurface.maturity)}</strong>
        </div>
      </aside>

      <footer className="activity-feedback" data-action-feedback>
        <span>{actionFeedback}</span>
      </footer>
    </main>
  )
}

function SurfaceHeader({
  surface,
  manifestEnabled,
  runtimeConnected
}: {
  surface: ActivitySurface
  manifestEnabled: boolean
  runtimeConnected: boolean
}): ReactElement {
  return (
    <header className="surface-header">
      <div>
        <span className="label">{surface.group}</span>
        <div className="surface-title-row">
          <SurfaceIcon surface={surface} />
          <h2>{surface.title}</h2>
        </div>
        <p>{surface.description}</p>
      </div>
      <div className="surface-badges">
        <span>{surface.audience}</span>
        <span>{manifestEnabled ? 'Manifesto OK' : 'Fora do manifesto'}</span>
        <span>{runtimeConnected ? 'Runtime pronto' : 'Preview seguro'}</span>
      </div>
    </header>
  )
}

function SurfaceWorkbench({
  surface,
  context,
  onSelectSurface,
  onActionFeedback
}: {
  surface: ActivitySurface
  context: DiscordActivityContext
  onSelectSurface: (surface: ActivitySurface) => void
  onActionFeedback: (message: string) => void
}): ReactElement {
  const [activeTab, setActiveTab] = useState<'overview' | 'commands' | 'tests'>(
    'overview'
  )
  const [compact, setCompact] = useState(false)
  const [selectedCommand, setSelectedCommand] = useState(surface.commands[0] ?? '')
  const fallbackSurfaceData = useMemo(() => surfaceDataFor(surface), [surface])
  const [runtimeSurfaceData, setRuntimeSurfaceData] =
    useState<SurfaceDataPayload | null>(null)
  const surfaceData = runtimeSurfaceData ?? fallbackSurfaceData
  const [selectedView, setSelectedView] = useState(
    surfaceData.views[0]?.key ?? 'summary'
  )
  const [query, setQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')

  useEffect(() => {
    setSelectedCommand(surface.commands[0] ?? '')
    setActiveTab('overview')
    setSelectedView(surfaceData.views[0]?.key ?? 'summary')
    setQuery('')
    setStatusFilter('all')
  }, [surface.key, surface.commands, surfaceData])

  useEffect(() => {
    let mounted = true
    setRuntimeSurfaceData(null)
    loadActivitySurfaceData(surface, context).then((data) => {
      if (!mounted || !data) return
      setRuntimeSurfaceData(data)
      onActionFeedback(`${surface.title}: snapshot runtime carregado.`)
    })
    return () => {
      mounted = false
    }
  }, [surface, context, onActionFeedback])

  function runAction(action: ActivityAction): void {
    const blockedReason = actionBlockedReason(surface, action)
    if (blockedReason) return
    if (action.label === 'Abrir Matchroom') {
      onSelectSurface(surfaceByKey('matchroom'))
      return
    }
    const guard =
      action.kind === 'admin'
        ? 'acao admin exige permissao e auditoria no backend'
        : action.kind === 'member'
          ? 'acao de membro deve validar Discord user no servidor'
          : action.kind === 'command'
            ? 'acao command-first deve abrir/instruir o comando'
            : 'acao de leitura executada no cliente'
    onActionFeedback(`${surface.title}: ${action.label} selecionado; ${guard}.`)
  }

  return (
    <div className={compact ? 'surface-workbench compact' : 'surface-workbench'}>
      <div className="surface-toolbar">
        <button
          className={activeTab === 'overview' ? 'selected' : ''}
          data-activity-button={`${surface.key}-tab-overview`}
          onClick={() => {
            setActiveTab('overview')
            onActionFeedback(`${surface.title}: aba resumo.`)
          }}
        >
          Resumo
        </button>
        <button
          className={activeTab === 'commands' ? 'selected' : ''}
          data-activity-button={`${surface.key}-tab-commands`}
          onClick={() => {
            setActiveTab('commands')
            onActionFeedback(`${surface.title}: aba comandos.`)
          }}
        >
          Comandos
        </button>
        <button
          className={activeTab === 'tests' ? 'selected' : ''}
          data-activity-button={`${surface.key}-tab-tests`}
          onClick={() => {
            setActiveTab('tests')
            onActionFeedback(`${surface.title}: aba testes.`)
          }}
        >
          Testes
        </button>
        <button
          className={compact ? 'selected' : ''}
          data-activity-button={`${surface.key}-compact-toggle`}
          onClick={() => {
            setCompact((value) => !value)
            onActionFeedback(`${surface.title}: densidade alternada.`)
          }}
        >
          Compacto
        </button>
      </div>

      {activeTab === 'overview' ? (
        <section className="surface-overview">
          <div className="surface-grid">
            <div className="surface-card span-2">
              <span className="label">O que vira Activity</span>
              <p>{surfaceData.headline}</p>
              <div className="action-row">
                {surface.actions.map((action) => {
                  const blockedReason = actionBlockedReason(surface, action)
                  return (
                    <button
                      key={action.label}
                      data-activity-button={`${surface.key}-action-${slug(action.label)}`}
                      data-disabled-reason={blockedReason}
                      disabled={Boolean(blockedReason)}
                      title={blockedReason}
                      onClick={() => runAction(action)}
                    >
                      {action.label}
                    </button>
                  )
                })}
              </div>
            </div>
            {surface.metrics.map((metric) => (
              <div key={metric} className="surface-card metric-card">
                <span className="label">Sinal</span>
                <strong>{metric}</strong>
              </div>
            ))}
          </div>
          <SurfaceDataExplorer
            surface={surface}
            data={surfaceData}
            selectedView={selectedView}
            onSelectedView={setSelectedView}
            query={query}
            onQueryChange={setQuery}
            statusFilter={statusFilter}
            onStatusFilterChange={setStatusFilter}
            onActionFeedback={onActionFeedback}
          />
        </section>
      ) : null}

      {activeTab === 'commands' ? (
        <section className="command-panel">
          <div className="command-list">
            {surface.commands.map((command) => (
              <button
                key={command}
                className={command === selectedCommand ? 'selected' : ''}
                data-activity-button={`${surface.key}-command-${slug(command)}`}
                onClick={() => {
                  setSelectedCommand(command)
                  onActionFeedback(`${surface.title}: ${command} selecionado.`)
                }}
              >
                {command}
              </button>
            ))}
          </div>
          <article className="surface-card command-detail">
            <span className="label">Comando selecionado</span>
            <strong>{selectedCommand}</strong>
            <p>
              A Activity deve servir este fluxo com estado visual, mantendo o
              comando como launcher, fallback ou trilha de auditoria.
            </p>
          </article>
        </section>
      ) : null}

      {activeTab === 'tests' ? (
        <section className="surface-grid">
          {surface.testingFocus.map((item) => (
            <button
              key={item}
              className="test-card"
              data-activity-button={`${surface.key}-test-${slug(item)}`}
              onClick={() =>
                onActionFeedback(`${surface.title}: teste '${item}' marcado.`)
              }
            >
              <span>Teste</span>
              <strong>{item}</strong>
            </button>
          ))}
          <button
            className="test-card accent"
            data-activity-button={`${surface.key}-test-open-matchroom`}
            onClick={() => onSelectSurface(surfaceByKey('matchroom'))}
          >
            <span>Regressao</span>
            <strong>Voltar ao fluxo ativo</strong>
          </button>
        </section>
      ) : null}
    </div>
  )
}

function SurfaceDataExplorer({
  surface,
  data,
  selectedView,
  onSelectedView,
  query,
  onQueryChange,
  statusFilter,
  onStatusFilterChange,
  onActionFeedback
}: {
  surface: ActivitySurface
  data: SurfaceDataPayload
  selectedView: string
  onSelectedView: (key: string) => void
  query: string
  onQueryChange: (value: string) => void
  statusFilter: string
  onStatusFilterChange: (value: string) => void
  onActionFeedback: (message: string) => void
}): ReactElement {
  const currentView =
    data.views.find((view) => view.key === selectedView) ?? data.views[0]
  const statuses = uniqueStatuses(data.views)
  const normalizedQuery = query.trim().toLowerCase()
  const filteredItems = (currentView?.items ?? []).filter((item) => {
    const matchesStatus = statusFilter === 'all' || item.status === statusFilter
    if (!matchesStatus) return false
    if (!normalizedQuery) return true
    return [item.title, item.meta, item.status, item.detail].some((value) =>
      value.toLowerCase().includes(normalizedQuery)
    )
  })

  return (
    <section className="surface-data-panel">
      <div className="data-toolbar">
        <div className="data-view-row" aria-label="Views da superficie">
          {data.views.map((view) => (
            <button
              key={view.key}
              className={view.key === currentView?.key ? 'selected' : ''}
              data-activity-button={`${surface.key}-view-${view.key}`}
              onClick={() => {
                onSelectedView(view.key)
                onActionFeedback(`${surface.title}: view ${view.title}.`)
              }}
            >
              {view.title}
            </button>
          ))}
        </div>
        <label className="inline-search">
          <span>Filtrar</span>
          <input
            data-activity-input={`${surface.key}-search`}
            type="search"
            value={query}
            placeholder="buscar"
            onChange={(event) => {
              onQueryChange(event.target.value)
              onActionFeedback(`${surface.title}: filtro atualizado.`)
            }}
          />
        </label>
        <label className="inline-search">
          <span>Status</span>
          <select
            data-activity-input={`${surface.key}-status-filter`}
            value={statusFilter}
            onChange={(event) => {
              onStatusFilterChange(event.target.value)
              onActionFeedback(`${surface.title}: status filtrado.`)
            }}
          >
            <option value="all">Todos</option>
            {statuses.map((status) => (
              <option key={status} value={status}>
                {status}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div className="data-summary">
        <div>
          <span className="label">{currentView?.title ?? 'Resumo'}</span>
          <p>{currentView?.summary ?? data.headline}</p>
        </div>
        <div className="data-status">
          <strong>{filteredItems.length} itens</strong>
          {data.source === 'bot-runtime' ? <span>Runtime bot</span> : null}
        </div>
      </div>

      <div className="data-list">
        {filteredItems.length ? (
          filteredItems.map((item) => (
            <article key={`${currentView?.key}-${item.title}`} className="data-row">
              <div>
                <strong>{item.title}</strong>
                <span>{item.meta}</span>
              </div>
              <span className="status-pill">{item.status}</span>
              <p>{item.detail}</p>
            </article>
          ))
        ) : (
          <article className="data-row empty">
            <strong>Nenhum item no filtro atual</strong>
            <p>Limpe a busca ou volte para status Todos.</p>
          </article>
        )}
      </div>

      <div className="guard-grid">
        <section>
          <span className="label">Saude / leitura</span>
          <p>{data.health}</p>
        </section>
        <section>
          <span className="label">Guardas</span>
          <ul>
            {data.safeguards.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </section>
        <section>
          <span className="label">Backend seguinte</span>
          <ul>
            {data.nextBackend.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </section>
      </div>
    </section>
  )
}

function SurfaceIcon({ surface }: { surface: ActivitySurface }): ReactElement {
  const variant = surfaceIconVariant(surface)
  return (
    <span className="surface-icon" data-icon={variant} aria-hidden="true">
      <svg viewBox="0 0 24 24" focusable="false">
        {surfaceIconPath(variant)}
      </svg>
    </span>
  )
}

type SurfaceIconVariant =
  | 'crosshair'
  | 'home'
  | 'profile'
  | 'ranking'
  | 'calendar'
  | 'wallet'
  | 'play'
  | 'queue'
  | 'team'
  | 'trophy'
  | 'media'
  | 'report'
  | 'server'
  | 'wave'
  | 'brand'
  | 'admin'

function surfaceIconVariant(surface: ActivitySurface): SurfaceIconVariant {
  if (surface.key === 'matchroom' || surface.key === 'map-veto') return 'crosshair'
  if (surface.key === 'home' || surface.key === 'server-guide') return 'home'
  if (surface.key === 'profiles' || surface.key === 'coaching') return 'profile'
  if (surface.key === 'leaderboards' || surface.key === 'seasons') return 'ranking'
  if (surface.key === 'pro-cs') return 'calendar'
  if (surface.key === 'inventory' || surface.key === 'economy') return 'wallet'
  if (surface.key === 'demo-review') return 'play'
  if (surface.key === 'matchmaking') return 'queue'
  if (surface.key === 'teams' || surface.key === 'scrims') return 'team'
  if (
    surface.key === 'tournaments' ||
    surface.key === 'pickems' ||
    surface.key === 'hall-of-fame'
  ) {
    return 'trophy'
  }
  if (surface.key === 'clips' || surface.key === 'twitch' || surface.key === 'soundboard') {
    return 'media'
  }
  if (surface.key === 'reports' || surface.key === 'roadmap') return 'report'
  if (surface.key === 'source-servers') return 'server'
  if (surface.key === 'brand') return 'brand'
  if (surface.key === 'admin') return 'admin'
  return 'wave'
}

function surfaceIconPath(variant: SurfaceIconVariant): ReactElement {
  const strokeProps = {
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.7,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const
  }

  if (variant === 'crosshair') {
    return (
      <>
        <circle cx="12" cy="12" r="6.2" {...strokeProps} />
        <path d="M12 3.5v4M12 16.5v4M3.5 12h4M16.5 12h4" {...strokeProps} />
        <circle cx="12" cy="12" r="1.5" fill="currentColor" />
      </>
    )
  }
  if (variant === 'home') {
    return (
      <>
        <path d="M5 11.5 12 5l7 6.5" {...strokeProps} />
        <path d="M7.5 10.5v8h9v-8" {...strokeProps} />
        <path d="M10.2 18.5v-4h3.6v4" {...strokeProps} />
      </>
    )
  }
  if (variant === 'profile') {
    return (
      <>
        <circle cx="9.2" cy="9" r="3" {...strokeProps} />
        <path d="M4.5 18.8c.8-3 2.4-4.4 4.7-4.4s3.9 1.4 4.7 4.4" {...strokeProps} />
        <path d="M15 8.2c1.9.2 3.1 1.5 3.1 3.2 0 1.6-1 2.7-2.6 3" {...strokeProps} />
      </>
    )
  }
  if (variant === 'ranking') {
    return (
      <>
        <path d="M5.5 18.5V13M12 18.5V7M18.5 18.5v-8" {...strokeProps} />
        <path d="M4 18.5h16" {...strokeProps} />
      </>
    )
  }
  if (variant === 'calendar') {
    return (
      <>
        <rect x="5" y="6" width="14" height="13" rx="2" {...strokeProps} />
        <path d="M8 4.5v3M16 4.5v3M5 10h14" {...strokeProps} />
        <path d="M8 13h2M13 13h3M8 16h5" {...strokeProps} />
      </>
    )
  }
  if (variant === 'wallet') {
    return (
      <>
        <path d="M5 8.5h13.5a1.5 1.5 0 0 1 1.5 1.5v7a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V7.2A2.2 2.2 0 0 1 6.7 5l9.3 2" {...strokeProps} />
        <path d="M16 13h4v3h-4a1.5 1.5 0 0 1 0-3Z" {...strokeProps} />
      </>
    )
  }
  if (variant === 'play') {
    return (
      <>
        <rect x="5" y="5" width="14" height="14" rx="2.5" {...strokeProps} />
        <path d="m10 9 5 3-5 3V9Z" fill="currentColor" />
      </>
    )
  }
  if (variant === 'queue') {
    return (
      <>
        <path d="M5 7h10M5 12h14M5 17h8" {...strokeProps} />
        <path d="m16 6 3 3-3 3" {...strokeProps} />
      </>
    )
  }
  if (variant === 'team') {
    return (
      <>
        <circle cx="8" cy="9" r="2.5" {...strokeProps} />
        <circle cx="16" cy="9" r="2.5" {...strokeProps} />
        <path d="M4.5 18c.7-2.5 1.9-3.6 3.5-3.6s2.8 1.1 3.5 3.6" {...strokeProps} />
        <path d="M12.5 18c.7-2.5 1.9-3.6 3.5-3.6s2.8 1.1 3.5 3.6" {...strokeProps} />
      </>
    )
  }
  if (variant === 'trophy') {
    return (
      <>
        <path d="M8 5h8v4.5a4 4 0 0 1-8 0V5Z" {...strokeProps} />
        <path d="M8 7H5.5v1.8A3.2 3.2 0 0 0 8.7 12M16 7h2.5v1.8a3.2 3.2 0 0 1-3.2 3.2M12 13.5v3M9 19h6" {...strokeProps} />
      </>
    )
  }
  if (variant === 'media') {
    return (
      <>
        <path d="M5 16c1.5-2.6 1.5-5.4 0-8M9 18c2.2-3.8 2.2-8.2 0-12M13 16c1.5-2.6 1.5-5.4 0-8" {...strokeProps} />
        <path d="M16 8.5 20 12l-4 3.5v-7Z" fill="currentColor" />
      </>
    )
  }
  if (variant === 'report') {
    return (
      <>
        <path d="M7 4.5h7l3 3V19H7V4.5Z" {...strokeProps} />
        <path d="M14 4.5V8h3M9.5 11h5M9.5 14h5M9.5 17h3" {...strokeProps} />
      </>
    )
  }
  if (variant === 'server') {
    return (
      <>
        <rect x="5" y="5" width="14" height="5" rx="1.5" {...strokeProps} />
        <rect x="5" y="14" width="14" height="5" rx="1.5" {...strokeProps} />
        <path d="M8 7.5h.1M8 16.5h.1M12 10v4" {...strokeProps} />
      </>
    )
  }
  if (variant === 'brand') {
    return (
      <>
        <path d="M12 4.5 18.5 8v8L12 19.5 5.5 16V8L12 4.5Z" {...strokeProps} />
        <path d="M8.7 12h6.6M12 8.7v6.6" {...strokeProps} />
      </>
    )
  }
  return (
    <>
      <path d="M8 18V6M12 18V6M16 18V6" {...strokeProps} />
      <path d="M5.5 9h13M5.5 15h13" {...strokeProps} />
    </>
  )
}

function MatchroomSurface({
  context,
  activityConfig,
  mapOnly,
  onActionFeedback
}: {
  context: DiscordActivityContext
  activityConfig: ActivityRuntimeConfig
  mapOnly: boolean
  onActionFeedback: (message: string) => void
}): ReactElement {
  const [snapshot, setSnapshot] = useState<MatchroomSnapshot>(previewSnapshot)
  const [maps, setMaps] = useState<MapPoolItem[]>(previewSnapshot.maps)
  const [mvpVote, setMvpVote] = useState(
    previewSnapshot.recap.mvpCandidates[0]?.candidateKey ?? ''
  )
  const [demoOptIn, setDemoOptIn] = useState(previewSnapshot.recap.demoOptIn)
  const [clipsOptIn, setClipsOptIn] = useState(previewSnapshot.recap.clipsOptIn)
  const [loadWarning, setLoadWarning] = useState<string | null>(null)
  const [ready, setReady] = useState(false)
  const [pendingAction, setPendingAction] = useState<string | null>(null)
  const [modeView, setModeView] = useState('5v5')

  useEffect(() => {
    let mounted = true
    loadMatchroomSnapshot(context).then((loaded) => {
      if (!mounted) return
      setSnapshot(loaded.snapshot)
      setMaps(loaded.snapshot.maps)
      setMvpVote(loaded.snapshot.recap.mvpCandidates[0]?.candidateKey ?? '')
      setDemoOptIn(loaded.snapshot.recap.demoOptIn)
      setClipsOptIn(loaded.snapshot.recap.clipsOptIn)
      setLoadWarning(loaded.warning ?? null)
    })
    return () => {
      mounted = false
    }
  }, [context])

  const teams = useMemo(() => snapshot.teams, [snapshot])
  const minutesLeft = Math.max(
    0,
    Math.ceil((snapshot.room.expiresAt - Math.floor(Date.now() / 1000)) / 60)
  )
  const recapResult = snapshot.recap.result
  const matchroomFinalized = Boolean(recapResult) || snapshot.room.status !== 'aberta'
  const runtimeConnected = snapshot.source === 'bot'
  const canMutate = runtimeConnected && !matchroomFinalized
  const disabledReason = canMutate
    ? undefined
    : runtimeConnected
      ? 'Matchroom encerrada ou finalizada.'
      : 'Runtime do bot indisponivel no preview local.'
  const mvpDisabledReason = !canMutate
    ? disabledReason
    : !mvpVote
      ? 'Selecione um MVP antes de votar.'
      : undefined
  const connectionLabel = loadWarning ?? (runtimeConnected ? context.label : 'Preview sem runtime')
  const readyCount = Math.min(
    snapshot.room.capacity,
    snapshot.room.readyCount + (ready && canMutate ? 1 : 0)
  )
  const activityStatus =
    snapshot.room.activity.enabled && runtimeConnected ? 'Habilitada' : 'Bloqueada'
  const statusLabel = recapResult
    ? 'Partida finalizada'
    : snapshot.room.status === 'aberta'
      ? 'Lobby aberto'
      : 'Lobby encerrado'
  const maxBalance = Math.max(
    1,
    snapshot.room.balance.teamAScore + snapshot.room.balance.teamBScore
  )

  function applyLoadedSnapshot(loaded: Awaited<ReturnType<typeof mutateMatchroom>>): void {
    setSnapshot(loaded.snapshot)
    setMaps(loaded.snapshot.maps)
    setMvpVote(loaded.snapshot.recap.mvpCandidates[0]?.candidateKey ?? '')
    setDemoOptIn(loaded.snapshot.recap.demoOptIn)
    setClipsOptIn(loaded.snapshot.recap.clipsOptIn)
    setLoadWarning(loaded.warning ?? null)
  }

  async function runAction(
    action: Parameters<typeof mutateMatchroom>[1],
    body: Record<string, unknown> = {}
  ): Promise<void> {
    if (!canMutate || pendingAction) {
      onActionFeedback(disabledReason ?? 'Acao bloqueada.')
      return
    }
    setPendingAction(action)
    onActionFeedback(`Matchroom: executando ${action}.`)
    const loaded = await mutateMatchroom(context, action, body)
    applyLoadedSnapshot(loaded)
    setPendingAction(null)
  }

  function cycleMap(key: string): void {
    void runAction('map', { mapKey: key })
  }

  return (
    <div className={mapOnly ? 'matchroom-layout map-focus' : 'matchroom-layout'}>
      <section className="left-rail">
        <div className="room-status">
          <span className="label">Status</span>
          <strong>{statusLabel}</strong>
          <p>
            {readyCount} prontos · {snapshot.room.playerCount}/{snapshot.room.capacity}{' '}
            jogadores · {activityStatus}
          </p>
        </div>
        <div className="mode-stack" aria-label="Modos">
          {['5v5', '2v2', 'Retake', 'Review'].map((mode) => (
            <button
              key={mode}
              className={modeView === mode ? 'selected' : ''}
              data-activity-button={`matchroom-mode-${slug(mode)}`}
              onClick={() => {
                setModeView(mode)
                onActionFeedback(`Matchroom: visualizando modo ${mode}.`)
              }}
            >
              {mode}
            </button>
          ))}
        </div>
        <div className="balance-meter">
          <span className="label">Balanceamento</span>
          <div className="meter-row">
            <span>Team A</span>
            <strong>{Math.round(snapshot.room.balance.teamAScore)}</strong>
          </div>
          <div className="meter">
            <span
              style={{
                width: `${Math.round(
                  (snapshot.room.balance.teamAScore / maxBalance) * 100
                )}%`
              }}
            />
          </div>
          <div className="meter-row">
            <span>Team B</span>
            <strong>{Math.round(snapshot.room.balance.teamBScore)}</strong>
          </div>
        </div>
        <div className="settings-list">
          <span className="label">Configuracoes</span>
          <div>
            <span>Formato</span>
            <strong>{snapshot.room.label}</strong>
          </div>
          <div>
            <span>Expira em</span>
            <strong>{minutesLeft} min</strong>
          </div>
          <div>
            <span>Activity</span>
            <strong>{activityStatus}</strong>
          </div>
        </div>
      </section>

      <section className="team-grid">
        {mapOnly ? null : (
          <TeamPanel
            title="Time A"
            players={teams.A}
            accent="blue"
            actionDisabled={!canMutate}
            disabledReason={disabledReason}
          />
        )}
        <MapVeto maps={maps} locked={!canMutate} disabledReason={disabledReason} onCycle={cycleMap} />
        {mapOnly ? null : (
          <TeamPanel
            title="Time B"
            players={teams.B}
            accent="amber"
            actionDisabled={!canMutate}
            disabledReason={disabledReason}
          />
        )}
      </section>

      {mapOnly ? null : (
        <aside className="right-rail">
          <section className="voice-panel">
            <h2>Salas de voz</h2>
            {snapshot.voiceRooms.map((room) => (
              <div key={room.team} className="voice-row">
                <span>{room.label}</span>
                <strong>{voiceStatusLabel(room.status)}</strong>
              </div>
            ))}
            <button
              className="secondary"
              data-activity-button="matchroom-voice"
              data-disabled-reason={!canMutate ? disabledReason : undefined}
              disabled={!canMutate || pendingAction === 'voice'}
              title={!canMutate ? disabledReason : undefined}
              onClick={() => void runAction('voice')}
            >
              Preparar salas
            </button>
          </section>

          <section className="recap-panel">
            <h2>Resumo</h2>
            {recapResult ? (
              <div className="recap-result">
                <span>Time {recapResult.winner} venceu</span>
                <strong>
                  {recapResult.teamARounds} x {recapResult.teamBRounds}
                </strong>
                <p>MVP: {recapResult.mvpDisplayName ?? 'nao definido'}</p>
                {recapResult.highlight ? <p>{recapResult.highlight}</p> : null}
              </div>
            ) : (
              <>
                <label>
                  <input
                    data-activity-input="matchroom-demo-optin"
                    type="checkbox"
                    checked={demoOptIn}
                    disabled={!canMutate}
                    title={!canMutate ? disabledReason : undefined}
                    onChange={(event) => {
                      setDemoOptIn(event.target.checked)
                      void runAction('demo')
                    }}
                  />
                  Demo opt-in
                </label>
                <label>
                  <input
                    data-activity-input="matchroom-clips-optin"
                    type="checkbox"
                    checked={clipsOptIn}
                    disabled={!canMutate}
                    title={!canMutate ? disabledReason : undefined}
                    onChange={(event) => {
                      setClipsOptIn(event.target.checked)
                      void runAction('clips')
                    }}
                  />
                  Clips opt-in
                </label>
                <select
                  data-activity-input="matchroom-mvp-select"
                  value={mvpVote}
                  disabled={!canMutate}
                  title={!canMutate ? disabledReason : undefined}
                  onChange={(event) => {
                    setMvpVote(event.target.value)
                    onActionFeedback(`Matchroom: MVP selecionado.`)
                  }}
                >
                  {snapshot.recap.mvpCandidates.map((player) => (
                    <option key={player.candidateKey} value={player.candidateKey}>
                      {player.displayName}
                    </option>
                  ))}
                </select>
                <button
                  className="secondary"
                  data-activity-button="matchroom-mvp"
                  data-disabled-reason={mvpDisabledReason}
                  disabled={!canMutate || !mvpVote || pendingAction === 'mvp'}
                  title={mvpDisabledReason}
                  onClick={() => void runAction('mvp', { candidateKey: mvpVote })}
                >
                  Votar MVP
                </button>
              </>
            )}
            <div className="recap-status">
              <span>Status</span>
              <strong>{recapResult ? 'Recap fechado' : 'Demo gravando'}</strong>
            </div>
          </section>
          <section className="mvp-panel">
            <h2>Previa MVP</h2>
            {snapshot.recap.mvpCandidates.map((item) => (
              <div key={item.displayName} className="mvp-row">
                <span>{item.displayName}</span>
                <div className="bar">
                  <span style={{ width: `${item.votes}%` }} />
                </div>
                <strong>{item.votes}%</strong>
              </div>
            ))}
          </section>
        </aside>
      )}

      <footer className="matchroom-log">
        <span>{connectionLabel}</span>
        <span>
          Worker: {activityConfig.activity.runtimeConfigured ? 'configurado' : 'preview'}
        </span>
        {snapshot.logLines.map((line) => (
          <span key={line}>{line}</span>
        ))}
      </footer>

      <button
        className={ready ? 'floating-ready ready' : 'floating-ready'}
        data-activity-button="matchroom-ready"
        data-disabled-reason={!canMutate ? disabledReason : undefined}
        disabled={!canMutate}
        title={!canMutate ? disabledReason : undefined}
        onClick={() => {
          setReady((value) => !value)
          void runAction('ready')
        }}
      >
        {pendingAction === 'ready' ? '...' : ready ? 'Pronto' : 'Marcar pronto'}
      </button>
    </div>
  )
}

function TeamPanel({
  title,
  players,
  accent,
  actionDisabled,
  disabledReason
}: {
  title: string
  players: ActivityPlayer[]
  accent: 'blue' | 'amber'
  actionDisabled: boolean
  disabledReason?: string
}): ReactElement {
  return (
    <section className={`team-panel ${accent}`}>
      <div className="panel-title">
        <h2>{title}</h2>
        <span>{players.filter((player) => player.ready === 'pronto').length}/5</span>
      </div>
      <div className="player-list">
        {players.map((player) => (
          <article key={player.displayName} className="player-row">
            <div className="avatar">
              {player.displayName.slice(0, 2).toUpperCase()}
            </div>
            <div className="player-copy">
              <strong>{player.displayName}</strong>
              <span>
                {player.role} · {player.rating}
              </span>
            </div>
            <span className={`ready-state ${player.ready}`}>{labelReady(player.ready)}</span>
          </article>
        ))}
      </div>
      <button
        className="team-action"
        data-activity-button={`matchroom-${slug(title)}-invite`}
        data-disabled-reason={actionDisabled ? disabledReason : undefined}
        disabled={actionDisabled}
        title={actionDisabled ? disabledReason : undefined}
      >
        Convidar / substitutos
      </button>
    </section>
  )
}

function MapVeto({
  maps,
  locked,
  disabledReason,
  onCycle
}: {
  maps: MapPoolItem[]
  locked: boolean
  disabledReason?: string
  onCycle: (key: string) => void
}): ReactElement {
  return (
    <section className="veto-panel">
      <div className="panel-title">
        <h2>Veto de mapas</h2>
        <span>BO1</span>
      </div>
      <div className="map-stack">
        {maps.map((map) => (
          <button
            key={map.key}
            className={`map-chip ${map.state}`}
            data-activity-button={`matchroom-map-${map.key}`}
            data-disabled-reason={locked ? disabledReason : undefined}
            disabled={locked}
            title={locked ? disabledReason : undefined}
            onClick={() => onCycle(map.key)}
          >
            <span>{map.name}</span>
            <strong>{labelMap(map.state)}</strong>
          </button>
        ))}
      </div>
    </section>
  )
}

function CoverageSummary(): ReactElement {
  const active = activitySurfaces.filter((surface) => surface.maturity === 'active').length
  const readOnly = activitySurfaces.filter((surface) => surface.maturity === 'read-only-next').length
  const mutations = activitySurfaces.filter((surface) => surface.maturity === 'member-mutation-next').length
  const commandFirst = activitySurfaces.filter((surface) => surface.maturity === 'command-first').length
  const admin = activitySurfaces.filter((surface) => surface.maturity === 'admin-last').length

  return (
    <div className="coverage-stats">
      <div>
        <strong>{active}</strong>
        <span>ativo</span>
      </div>
      <div>
        <strong>{readOnly}</strong>
        <span>read-only</span>
      </div>
      <div>
        <strong>{mutations}</strong>
        <span>mutacoes</span>
      </div>
      <div>
        <strong>{commandFirst + admin}</strong>
        <span>gated</span>
      </div>
    </div>
  )
}

function actionBlockedReason(
  surface: ActivitySurface,
  action: ActivityAction
): string | undefined {
  if (action.disabledReason) return action.disabledReason
  if (action.kind === 'admin') {
    return 'Acoes admin exigem permissao server-side, confirmacao e auditoria.'
  }
  if (action.kind === 'member' && surface.maturity !== 'active') {
    return 'Mutacao de membro pendente de endpoint autenticado e checagem de guilda.'
  }
  if (action.kind === 'command') {
    return 'Fluxo command-first; use o comando como entrada confiavel.'
  }
  return undefined
}

function uniqueStatuses(views: SurfaceDataView[]): string[] {
  const statuses = new Set<string>()
  for (const view of views) {
    for (const item of view.items) statuses.add(item.status)
  }
  return [...statuses].sort((a, b) => a.localeCompare(b))
}

function maturityLabel(value: ActivitySurface['maturity']): string {
  if (value === 'active') return 'ativo'
  if (value === 'read-only-next') return 'API'
  if (value === 'member-mutation-next') return 'mutacao'
  if (value === 'admin-last') return 'admin'
  return 'comando'
}

function maturityDescription(value: ActivitySurface['maturity']): string {
  if (value === 'active') return 'Superficie ativa com runtime ou preview seguro.'
  if (value === 'read-only-next') return 'Migrar primeiro como API read-only.'
  if (value === 'member-mutation-next') return 'Adicionar depois de permissao server-side.'
  if (value === 'admin-last') return 'Implementar por ultimo com confirmacao e auditoria.'
  return 'Manter comando como entrada obrigatoria; Activity exibe resultado/historico.'
}

function labelReady(value: ReadyState): string {
  if (value === 'pronto') return 'pronto'
  if (value === 'ausente') return 'ausente'
  return 'wait'
}

function labelMap(value: MapState): string {
  if (value === 'ban') return 'ban'
  if (value === 'pick') return 'pick'
  return 'livre'
}

function voiceStatusLabel(value: 'standby' | 'ready' | 'unavailable'): string {
  if (value === 'ready') return 'pronta'
  if (value === 'unavailable') return 'indisponivel'
  return 'standby'
}

function canApplyLaunchContext(pathname: string): boolean {
  const normalized = pathname.replace(/\/+$/, '') || '/'
  return normalized === '/' || normalized === '/home'
}

function slug(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 48)
}
